import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircle as PlayCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { styled } from '@mui/material/styles';
import Button from '../UIKit/Button';

const SidebarContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  height: 'fit-content',
  width: '100%',
}));

const LessonNavigationSidebar = ({
  courseData,
  currentModule,
  currentLesson,
  moduleIndex,
  lessonIndex,
  onBackToCourse,
  onPrevLesson,
  onNextLesson,
  hasPrevLesson,
  hasNextLesson,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(['lessons', 'courses']);
  const { user } = useAuth();

  const [expandedModules, setExpandedModules] = useState(() => {
    // Открываем текущий модуль по умолчанию
    return moduleIndex !== null ? [moduleIndex] : [];
  });

  // Вычисляем прогресс курса
  const courseProgress = useMemo(() => {
    if (!courseData?.modules || !user) return { completed: 0, total: 0, percentage: 0 };

    let totalLessons = 0;
    let completedLessons = 0;

    courseData.modules.forEach((moduleRef) => {
      const module = moduleRef.module || moduleRef;
      if (module.lessons) {
        module.lessons.forEach((lesson) => {
          totalLessons++;
          // Проверяем завершенность урока через userProgress
          if (courseData?.userProgress?.completedLessons) {
            const lessonId = typeof lesson._id === 'string' ? lesson._id : lesson._id.toString();
            if (courseData.userProgress.completedLessons.includes(lessonId)) {
              completedLessons++;
            }
          } else if (lesson.completed) {
            completedLessons++;
          }
        });
      }
    });

    const percentage = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;

    return {
      completed: completedLessons,
      total: totalLessons,
      percentage: Math.round(percentage),
    };
  }, [courseData, user]);

  // Проверяем, заблокирован ли модуль
  const isModuleLocked = (module, moduleIdx) => {
    if (!user) return true;

    // Используем поле locked из API
    const moduleRef = courseData?.modules?.[moduleIdx];
    if (moduleRef && typeof moduleRef.locked === 'boolean') {
      return moduleRef.locked;
    }

    return false;
  };

  // Проверяем, завершен ли урок
  const isLessonCompleted = (lesson) => {
    // Сначала проверяем данные о прогрессе пользователя
    if (courseData?.userProgress?.completedLessons) {
      const lessonId = typeof lesson._id === 'string' ? lesson._id : lesson._id.toString();
      return courseData.userProgress.completedLessons.includes(lessonId);
    }
    // Fallback на старое поле completed
    return lesson.completed || false;
  };

  // Проверяем, является ли урок текущим
  const isCurrentLesson = (lesson, moduleIdx, lessonIdx) => {
    return moduleIdx === moduleIndex && lessonIdx === lessonIndex;
  };

  // Обработчик клика по уроку
  const handleLessonClick = (lesson, moduleIdx, lessonIdx) => {
    const module = courseData.modules[moduleIdx].module || courseData.modules[moduleIdx];
    const moduleSlug = module.slug || module._id;
    const lessonSlug = lesson.slug || lesson._id;

    navigate(`/courses/${courseData.slug}/modules/${moduleSlug}/lessons/${lessonSlug}`);
  };

  // Обработчик изменения состояния аккордеона
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedModules((prev) => {
      if (isExpanded) {
        return [...prev, panel];
      } else {
        return prev.filter((item) => item !== panel);
      }
    });
  };

  // Убираем проверку на мобильные устройства, так как теперь сайдбар должен отображаться везде
  // if (isMobile) {
  //   return null; // Не показываем на мобильных устройствах
  // }

  if (!courseData || !courseData.modules) {
    return null;
  }

  return (
    <SidebarContainer>
      {/* Навигационные кнопки */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={onBackToCourse}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{ mb: 1 }}
        >
          {t('lessonDetailPage.navigation.backToCourse')}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            sx={{ flex: 1 }}
            onClick={onPrevLesson}
            disabled={!hasPrevLesson}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          >
            {t('lessonDetailPage.navigation.previousLesson')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ flex: 1 }}
            onClick={onNextLesson}
            disabled={!hasNextLesson}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          >
            {t('lessonDetailPage.navigation.nextLesson')}
          </Button>
        </Box>
      </Box>

      {/* Информация о курсе и прогрессе */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            fontSize: '1.25rem',
            lineHeight: 1.2,
          }}
        >
          {courseData.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            color: 'text.secondary',
            mb: 2,
          }}
        >
          {user
            ? t('lessonDetailPage.sidebar.progress', {
                completed: courseProgress.completed,
                total: courseProgress.total,
              })
            : t('lessonDetailPage.sidebar.loginToSeeProgress')
          }
        </Typography>

        {user && (
          <Box sx={{
            width: '100%',
            backgroundColor: theme.palette.mode === 'dark' ? '#1a202c' : '#eeeeee',
            borderRadius: '9999px',
            height: '6px',
            mb: 3,
            overflow: 'hidden',
          }}>
            <Box sx={{
              height: '100%',
              borderRadius: '9999px',
              background: `linear-gradient(90deg, #63b3ed 0%, #4299e1 100%)`,
              width: `${courseProgress.percentage}%`,
              transition: 'width 0.3s ease',
            }} />
          </Box>
        )}
      </Box>

      {/* Модули курса */}
      <Box sx={{ '& > *:not(:last-child)': { mb: 0.5 } }}>
        {courseData.modules.map((moduleRef, moduleIdx) => {
          const module = moduleRef.module || moduleRef;
          const isExpanded = expandedModules.includes(moduleIdx);
          const isLocked = isModuleLocked(module, moduleIdx);
          const hasLessons = module.lessons && module.lessons.length > 0;
          const isCurrentModule = moduleIdx === moduleIndex;

          if (!hasLessons) return null;

          return (
            <Accordion
              key={module._id}
              expanded={isExpanded}
              onChange={handleAccordionChange(moduleIdx)}
              sx={{
                boxShadow: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                backgroundImage: 'none',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: '1.25rem',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                }
                sx={{
                  padding: theme.spacing(0.5, 1),
                  borderRadius: theme.spacing(1),
                  minHeight: '36px',
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                  },
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                    margin: 0,
                    padding: 0,
                  }}
                >
                  {t('lessonDetailPage.sidebar.module', { number: moduleIdx + 1 })}: {module.title}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{
                padding: 0,
                backgroundColor: 'transparent',
                backgroundImage: 'none'
              }}>
                <List
                  sx={{
                    padding: theme.spacing(theme.palette.mode === 'dark' ? 0.25 : 0.5, 0, 0, 1),
                    borderLeft: isCurrentModule ? `2px solid ${theme.palette.mode === 'dark' ? '#63b3ed' : '#4299e1'}` : 'none',
                    ml: 1,
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                  }}
                >
                  {module.lessons.map((lesson, lessonIdx) => {
                    const isCompleted = isLessonCompleted(lesson);
                    const isCurrent = isCurrentLesson(lesson, moduleIdx, lessonIdx);
                    const isClickable = !isLocked || isCompleted || isCurrent;

                    return (
                      <ListItem
                        key={lesson._id}
                        sx={{
                          padding: theme.spacing(0.75),
                          cursor: isClickable ? 'pointer' : 'default',
                          backgroundColor: isCurrent
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(99, 179, 237, 0.1)'
                              : 'rgba(66, 153, 225, 0.1)'
                            : 'transparent',
                          borderRadius: theme.spacing(0.5),
                          mb: 0.25,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isClickable && !isCurrent
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(99, 179, 237, 0.05)'
                                : 'rgba(66, 153, 225, 0.05)'
                              : undefined,
                          },
                        }}
                        onClick={() => isClickable && handleLessonClick(lesson, moduleIdx, lessonIdx)}
                      >
                        <ListItemIcon sx={{ minWidth: 26 }}>
                          {isLocked ? (
                            <LockIcon sx={{
                              fontSize: '0.9rem',
                              color: theme.palette.mode === 'dark' ? '#4a5568' : 'rgba(0,0,0,0.38)',
                            }} />
                          ) : isCompleted ? (
                            <CheckCircleIcon sx={{
                              fontSize: '0.9rem',
                              color: '#48bb78',
                            }} />
                          ) : isCurrent ? (
                            <PlayCircleIcon sx={{
                              fontSize: '0.9rem',
                              color: theme.palette.mode === 'dark' ? '#63b3ed' : '#3182ce',
                            }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{
                              fontSize: '0.9rem',
                              color: theme.palette.mode === 'dark' ? '#4a5568' : 'rgba(0,0,0,0.38)',
                            }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={lesson.title}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontSize: '0.875rem',
                            lineHeight: 1.3,
                            fontWeight: isCurrent ? 600 : 400,
                            color: isLocked
                              ? theme.palette.mode === 'dark' ? '#4a5568' : 'rgba(0,0,0,0.38)'
                              : isCurrent
                                ? theme.palette.mode === 'dark' ? '#63b3ed' : '#3182ce'
                                : 'text.primary',
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </SidebarContainer>
  );
};

export default LessonNavigationSidebar;
